const DEFAULT_SPEECH_RATE = 1;
const MIN_SPEECH_RATE = 0.5;
const MAX_SPEECH_RATE = 3;
const MAX_DASHBOARD_SECTIONS = 24;
const MAX_PLAN_CHARS_BY_MINUTES = Object.freeze({ 2: 3_200, 5: 8_000 });
const MAX_BRIEF_JOURNEY_PLAN_CHARS = 8_000;
const DEFAULT_VOICE_INVENTORY_WAIT_MS = 500;
const DEVICE_SPEECH_TIMELINE_TICK_MS = 200;
const DEVICE_SPEECH_ATTEMPT_START_TIMEOUT_MS = 5_000;
const ESTIMATED_SPEECH_WORDS_PER_MINUTE = 155;
const MIN_ESTIMATED_SPEECH_DURATION_MS = 4_000;
const TIMELINE_END_RESERVE_RATIO = 0.15;
const MIN_TIMELINE_END_RESERVE_MS = 500;
const MAX_TIMELINE_END_RESERVE_MS = 30_000;
const MIN_BOUNDARY_CALIBRATION_INTERVAL_MS = 75;
const RESTART_BOUNDARY_SCAN_CHARS = 96;

export const DEVICE_SPEECH_MAX_CHUNK_CHARS = 700;

const PRODUCT_SPEECH_LOCALES = Object.freeze({
  ar: "ar",
  arz: "arz-EG",
  as: "as-IN",
  bn: "bn-IN",
  de: "de-DE",
  en: "en-US",
  es: "es-ES",
  fr: "fr-FR",
  gu: "gu-IN",
  ha: "ha-NG",
  hi: "hi-IN",
  id: "id-ID",
  ja: "ja-JP",
  kn: "kn-IN",
  ml: "ml-IN",
  mr: "mr-IN",
  or: "or-IN",
  pa: "pa-IN",
  pcm: "pcm-NG",
  pnb: "pa-Arab-PK",
  pt: "pt-PT",
  ru: "ru-RU",
  sw: "sw-KE",
  ta: "ta-IN",
  te: "te-IN",
  tl: "fil-PH",
  tr: "tr-TR",
  ur: "ur-IN",
  vi: "vi-VN",
  yue: "zh-Hant-HK",
  zh: "zh-Hans-CN",
});

const MARKET_REGIONAL_LANGS = new Set(["en", "es", "fr"]);
const MARKET_COUNTRIES = new Set(["AU", "CA", "DE", "FR", "GB", "IN", "JP", "US"]);

function cleanText(value) {
  return String(value || "")
    .replace(/\u00a0/g, " ")
    .replace(/\[\[\s*pause\s*:\s*(?:750ms|0\.75s|1s)\s*\]\]/gi, " ")
    .replace(/[\r\n\t]+/g, " ")
    .replace(/[*_`#]+/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function cleanInline(value, maxChars) {
  return truncateAtBoundary(cleanText(value), maxChars);
}

function cleanKey(value, maxChars = 320) {
  return Array.from(
    String(value || "")
      .replace(/\s+/g, " ")
      .trim(),
  )
    .slice(0, maxChars)
    .join("");
}

function canonicalLocaleOrNull(value) {
  try {
    return Intl.getCanonicalLocales(value)[0] || null;
  } catch {
    return null;
  }
}

function localeLanguageAndScript(value) {
  const raw = String(value || "")
    .trim()
    .replaceAll("_", "-");
  const originalLanguage = raw.toLowerCase().split("-")[0];
  try {
    const locale = new Intl.Locale(raw);
    const maximized = locale.maximize();
    return {
      language: String(locale.language || "").toLowerCase(),
      originalLanguage,
      script: String(maximized.script || "").toLowerCase(),
      region: String(maximized.region || "").toLowerCase(),
      explicitRegion: String(locale.region || "").toLowerCase(),
      cantonese:
        String(maximized.script || "").toLowerCase() === "hant" &&
        String(maximized.region || "").toLowerCase() === "hk" &&
        (String(locale.language || "").toLowerCase() === "yue" ||
          (String(locale.language || "").toLowerCase() === "zh" &&
            String(locale.region || "").toLowerCase() === "hk")),
    };
  } catch {
    return {
      language: originalLanguage,
      originalLanguage,
      script: "",
      region: "",
      explicitRegion: "",
      cantonese: false,
    };
  }
}

function sameSpeechLanguageAndScript(wanted, candidate) {
  if (wanted.script && candidate.script && wanted.script !== candidate.script) return false;
  if (wanted.cantonese || candidate.cantonese) {
    return wanted.cantonese && candidate.cantonese;
  }

  if (wanted.language === candidate.language) {
    const originalLanguages = new Set([wanted.originalLanguage, candidate.originalLanguage]);
    if (originalLanguages.has("tl") && originalLanguages.has("fil")) {
      return wanted.region === "ph" && candidate.region === "ph";
    }
    return true;
  }

  const languages = new Set([wanted.language, candidate.language]);
  const originals = new Set([wanted.originalLanguage, candidate.originalLanguage]);
  const isShahmukhiAlias =
    wanted.script === "arab" &&
    candidate.script === "arab" &&
    [...languages, ...originals].every((language) => ["pa", "lah", "pnb"].includes(language));
  if (isShahmukhiAlias) return true;

  if (languages.has("ar") && languages.has("arz")) {
    const isExplicitEgyptianArabic = (locale) =>
      locale.script === "arab" &&
      locale.region === "eg" &&
      (locale.originalLanguage === "arz" ||
        (locale.language === "ar" && locale.explicitRegion === "eg"));
    return isExplicitEgyptianArabic(wanted) && isExplicitEgyptianArabic(candidate);
  }

  return false;
}

function speechLocale(lang, country) {
  const raw = String(lang || "")
    .trim()
    .replaceAll("_", "-");
  if (!raw) return null;
  const normalized = raw.toLowerCase();
  const base = normalized.split("-")[0];
  if (normalized.includes("-")) {
    const canonical = canonicalLocaleOrNull(raw);
    if (!canonical) return null;
    const locale = localeLanguageAndScript(canonical);
    const productLanguage = locale.cantonese
      ? "yue"
      : ["fil", "tl"].includes(locale.language)
        ? "tl"
        : ["lah", "pnb"].includes(locale.language) ||
            (locale.language === "pa" && locale.script === "arab")
          ? "pnb"
          : locale.language;
    const productLocale = PRODUCT_SPEECH_LOCALES[productLanguage];
    if (!productLocale) return null;
    return sameSpeechLanguageAndScript(localeLanguageAndScript(productLocale), locale)
      ? canonical
      : null;
  }
  const productLanguage = base === "fil" ? "tl" : base === "lah" ? "pnb" : base;
  if (!PRODUCT_SPEECH_LOCALES[productLanguage]) return null;
  const region = String(country || "")
    .trim()
    .toUpperCase();
  if (MARKET_REGIONAL_LANGS.has(productLanguage) && MARKET_COUNTRIES.has(region)) {
    return canonicalLocaleOrNull(`${productLanguage}-${region}`);
  }
  return PRODUCT_SPEECH_LOCALES[productLanguage];
}

export function resolveDeviceSpeechLocale(lang, country) {
  return speechLocale(lang, country);
}

function normalizeRate(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return DEFAULT_SPEECH_RATE;
  return Math.min(MAX_SPEECH_RATE, Math.max(MIN_SPEECH_RATE, numeric));
}

function truncateAtBoundary(value, maxChars) {
  const text = cleanText(value);
  const limit = Math.max(1, Math.floor(Number(maxChars) || 1));
  const codepoints = Array.from(text);
  if (codepoints.length <= limit) return text;
  const head = codepoints.slice(0, limit).join("");
  const minimumBoundary = Math.floor(head.length * 0.55);
  const boundaryPatterns = [". ", "! ", "? ", "। ", "。", "？", "！", "؟ ", "; ", ", ", " "];
  let boundary = -1;
  let boundaryWidth = 0;
  for (const pattern of boundaryPatterns) {
    const candidate = head.lastIndexOf(pattern);
    if (candidate >= minimumBoundary && candidate > boundary) {
      boundary = candidate;
      boundaryWidth = pattern.trim() ? pattern.trimEnd().length : 0;
    }
  }
  const clipped = boundary >= minimumBoundary ? head.slice(0, boundary + boundaryWidth) : head;
  return clipped.trim();
}

function splitSpeechChunk(value, maxChars = DEVICE_SPEECH_MAX_CHUNK_CHARS) {
  const text = cleanText(value);
  if (!text) return [];
  const chunks = [];
  let remaining = text;
  while (remaining) {
    const codepoints = Array.from(remaining);
    if (codepoints.length <= maxChars) {
      chunks.push(remaining);
      break;
    }
    const chunk = truncateAtBoundary(remaining, maxChars);
    if (!chunk) break;
    chunks.push(chunk);
    remaining = cleanText(codepoints.slice(Array.from(chunk).length).join(""));
  }
  return chunks;
}

function dashboardSectionText(section, allowance) {
  const title = cleanInline(section?.title, 160);
  const body = cleanText(section?.body);
  if (!body) return "";
  const prefix = title ? `${title}. ` : "";
  const bodyLimit = Math.max(48, allowance - Array.from(prefix).length);
  return `${prefix}${truncateAtBoundary(body, bodyLimit)}`.trim();
}

/**
 * Build a bounded, localized plan from the ordered Dashboard request only for
 * capability negotiation. Once the server returns `speechText`, that canonical
 * transcript replaces every provisional client-authored section.
 */
export function buildDashboardDeviceSpeechPlan(input = {}) {
  const locale = speechLocale(input.lang, input.country);
  if (!locale) return null;
  const targetMinutes = Number(input.targetMinutes) === 5 ? 5 : 2;
  const maxPlanChars = MAX_PLAN_CHARS_BY_MINUTES[targetMinutes];
  const hasAuthoritativeSpeechText = Object.prototype.hasOwnProperty.call(input, "speechText");
  let speechParts = [];
  if (hasAuthoritativeSpeechText) {
    const authoritativeSpeechText = cleanText(input.speechText);
    if (authoritativeSpeechText) speechParts = [authoritativeSpeechText];
  } else {
    const rawSections = Array.isArray(input.facts?.briefSections)
      ? input.facts.briefSections.slice(0, MAX_DASHBOARD_SECTIONS)
      : [];
    const validSections = rawSections.filter((section) => cleanText(section?.body));
    if (validSections.length > 0) {
      const perSectionAllowance = Math.max(96, Math.floor(maxPlanChars / validSections.length));
      speechParts = validSections
        .map((section) => dashboardSectionText(section, perSectionAllowance))
        .filter(Boolean);
    } else {
      const summary = truncateAtBoundary(input.smartSummaryText, maxPlanChars);
      if (summary) speechParts = [summary];
    }
  }
  const chunks = speechParts.flatMap((part) => splitSpeechChunk(part));
  if (chunks.length === 0) return null;
  return Object.freeze({
    key: cleanKey(input.key) || "dashboard-briefing",
    label: cleanInline(input.label, 240) || "Brief audio",
    detail: cleanInline(input.detail, 400) || undefined,
    lang: locale,
    chunks: Object.freeze(chunks),
  });
}

/**
 * Build a bounded plan for capability negotiation, then replace its text with
 * the canonical server transcript before authorizing device-speech playback.
 */
export function buildBriefJourneyDeviceSpeechPlan(input = {}) {
  const locale = speechLocale(input.lang || input.languageCode, input.country || input.countryCode);
  if (!locale) return null;
  const context =
    input.transcriptContext && typeof input.transcriptContext === "object"
      ? input.transcriptContext
      : {};
  const firstSection = Array.isArray(context.sections) ? context.sections[0] : null;
  const hasAuthoritativeSpeechText = Object.prototype.hasOwnProperty.call(input, "speechText");
  const authoritativeSpeechText = cleanText(input.speechText);
  const title = cleanInline(firstSection?.title || context.title, 240);
  const body = cleanText(
    firstSection?.body || firstSection?.summary || context.body || context.summary,
  );
  const sourceText = hasAuthoritativeSpeechText
    ? authoritativeSpeechText
    : [title, body].filter(Boolean).join(title && body ? ". " : "");
  const planText = hasAuthoritativeSpeechText
    ? sourceText
    : truncateAtBoundary(sourceText, MAX_BRIEF_JOURNEY_PLAN_CHARS);
  const chunks = splitSpeechChunk(planText);
  if (chunks.length === 0) return null;
  return Object.freeze({
    key: cleanKey(input.key) || "brief-journey",
    label: cleanInline(input.label, 240) || title || "Brief section",
    detail: cleanInline(input.detail, 400) || undefined,
    lang: locale,
    chunks: Object.freeze(chunks),
  });
}

/** Builds a plan exclusively from the canonical transcript returned by the server. */
export function buildCanonicalDeviceSpeechPlan(input = {}) {
  if (!Object.prototype.hasOwnProperty.call(input, "speechText")) return null;
  const locale = speechLocale(input.lang || input.languageCode, input.country || input.countryCode);
  if (!locale) return null;
  const chunks = splitSpeechChunk(input.speechText);
  if (chunks.length === 0) return null;
  return Object.freeze({
    key: cleanKey(input.key) || "device-speech",
    label: cleanInline(input.label, 240) || "Brief audio",
    detail: cleanInline(input.detail, 400) || undefined,
    lang: locale,
    chunks: Object.freeze(chunks),
  });
}

function normalizedPlan(value) {
  if (!value || typeof value !== "object") return null;
  const locale = speechLocale(value.lang);
  if (!locale) return null;
  const chunks = (Array.isArray(value.chunks) ? value.chunks : []).flatMap((chunk) =>
    splitSpeechChunk(chunk),
  );
  if (chunks.length === 0) return null;
  return {
    key: cleanKey(value.key) || "device-speech",
    label: cleanInline(value.label, 240) || "Brief audio",
    detail: cleanInline(value.detail, 400) || undefined,
    lang: locale,
    chunks,
  };
}

function countSpeechUnits(text, locale) {
  let segmentedWords = 0;
  try {
    const segmenter = new Intl.Segmenter(locale, { granularity: "word" });
    for (const segment of segmenter.segment(text)) {
      if (segment.isWordLike) segmentedWords += 1;
    }
  } catch {
    const words = text.match(/[\p{L}\p{N}'-]+/gu);
    segmentedWords = words ? words.length : 0;
  }
  const nonWhitespaceCharacters = Array.from(text.replace(/\s+/g, "")).length;
  return Math.max(segmentedWords, Math.ceil(nonWhitespaceCharacters / 6));
}

export function estimateDeviceSpeechDurationMs(plan) {
  const normalized = normalizedPlan(plan);
  if (!normalized) return 0;
  const speechUnits = countSpeechUnits(normalized.chunks.join(" "), normalized.lang);
  if (speechUnits <= 0) return 0;
  return Math.max(
    MIN_ESTIMATED_SPEECH_DURATION_MS,
    Math.ceil((speechUnits / ESTIMATED_SPEECH_WORDS_PER_MINUTE) * 60_000),
  );
}

function timelineSegmentsForPlan(plan) {
  let absoluteStart = 0;
  const segments = plan.chunks.map((text) => {
    const segment = {
      text,
      absoluteStart,
      absoluteEnd: absoluteStart + text.length,
    };
    absoluteStart = segment.absoluteEnd;
    return segment;
  });
  return { segments, totalTextLength: absoluteStart };
}

function graphemeSafeIndex(text, index) {
  const clamped = Math.min(text.length, Math.max(0, Math.floor(index)));
  if (clamped === text.length) return text.length;
  try {
    const segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });
    let priorIndex = 0;
    for (const segment of segmenter.segment(text)) {
      if (segment.index >= clamped) return segment.index;
      priorIndex = segment.index;
    }
    return priorIndex;
  } catch {
    if (
      clamped > 0 &&
      clamped < text.length &&
      /[\uDC00-\uDFFF]/.test(text[clamped]) &&
      /[\uD800-\uDBFF]/.test(text[clamped - 1])
    ) {
      return clamped - 1;
    }
    return clamped;
  }
}

function restartIndexForText(text, requestedIndex) {
  const safeIndex = graphemeSafeIndex(text, requestedIndex);
  if (safeIndex <= 0 || safeIndex >= text.length) return safeIndex;
  let candidate = safeIndex;
  let scanned = 0;
  while (candidate > 0 && scanned < RESTART_BOUNDARY_SCAN_CHARS) {
    if (/[\s.!?;,:।。？！؟،؛]/u.test(text[candidate - 1])) break;
    candidate -= 1;
    scanned += 1;
  }
  if (candidate === 0 || scanned >= RESTART_BOUNDARY_SCAN_CHARS) return safeIndex;
  while (candidate < text.length && /\s/u.test(text[candidate])) candidate += 1;
  return graphemeSafeIndex(text, candidate);
}

function speechEntriesFromPosition(segments, totalTextLength, durationMs, positionMs) {
  if (totalTextLength <= 0 || durationMs <= 0 || positionMs >= durationMs) return [];
  const targetTextIndex = Math.floor((Math.max(0, positionMs) / durationMs) * totalTextLength);
  const entries = [];
  for (const segment of segments) {
    if (targetTextIndex >= segment.absoluteEnd) continue;
    const requestedLocalIndex = Math.max(0, targetTextIndex - segment.absoluteStart);
    let localStart = restartIndexForText(segment.text, requestedLocalIndex);
    while (localStart < segment.text.length && /\s/u.test(segment.text[localStart])) {
      localStart += 1;
    }
    const text = segment.text.slice(localStart);
    if (text) {
      entries.push({
        text,
        absoluteStart: segment.absoluteStart + localStart,
        absoluteEnd: segment.absoluteEnd,
      });
    }
  }
  return entries;
}

function voiceResolutionForLocale(synth, locale) {
  let voices = [];
  try {
    voices = Array.from(synth.getVoices?.() || []);
  } catch {
    return { enumerated: false, voice: null };
  }
  const wanted = String(locale || "").toLowerCase();
  const wantedLocale = localeLanguageAndScript(wanted);
  const ranked = voices
    .map((voice, index) => {
      const voiceLang = String(voice?.lang || "").toLowerCase();
      const voiceLocale = localeLanguageAndScript(voiceLang);
      const exact = voiceLang === wanted;
      const sameLanguageAndScript = sameSpeechLanguageAndScript(wantedLocale, voiceLocale);
      const local = voice?.localService !== false;
      const score =
        (exact ? 100 : sameLanguageAndScript ? 50 : 0) +
        (local ? 10 : 0) +
        (voice?.default ? 1 : 0);
      return { voice, index, score };
    })
    .filter((entry) => entry.score >= 50)
    .sort((a, b) => b.score - a.score || a.index - b.index);
  return { enumerated: voices.length > 0, voice: ranked[0]?.voice || null };
}

function resolveRuntime(overrides) {
  const hasSynth = Object.prototype.hasOwnProperty.call(overrides, "speechSynthesis");
  const hasUtterance = Object.prototype.hasOwnProperty.call(overrides, "SpeechSynthesisUtterance");
  return {
    synth: hasSynth ? overrides.speechSynthesis : globalThis.speechSynthesis,
    Utterance: hasUtterance
      ? overrides.SpeechSynthesisUtterance
      : globalThis.SpeechSynthesisUtterance,
  };
}

function deviceSpeechRuntimeSupported(overrides) {
  const { synth, Utterance } = resolveRuntime(overrides);
  return Boolean(
    synth &&
    typeof Utterance === "function" &&
    typeof synth.cancel === "function" &&
    typeof synth.pause === "function" &&
    typeof synth.resume === "function" &&
    typeof synth.speak === "function" &&
    typeof synth.getVoices === "function",
  );
}

export function hasCompatibleDeviceSpeechVoice(locale, overrides = {}) {
  if (!deviceSpeechRuntimeSupported(overrides)) return false;
  const resolvedLocale = speechLocale(locale);
  if (!resolvedLocale) return false;
  const { synth } = resolveRuntime(overrides);
  return Boolean(voiceResolutionForLocale(synth, resolvedLocale).voice);
}

/**
 * Browsers commonly populate speech voices after page load. Wait once, within a
 * small request-time bound, but advertise no fallback unless an exact
 * language/script-compatible voice is actually enumerated.
 */
export async function waitForCompatibleDeviceSpeechVoice(
  locale,
  { signal, timeoutMs = DEFAULT_VOICE_INVENTORY_WAIT_MS, ...overrides } = {},
) {
  if (!deviceSpeechRuntimeSupported(overrides)) return false;
  const resolvedLocale = speechLocale(locale);
  if (!resolvedLocale) return false;
  const { synth } = resolveRuntime(overrides);
  const initialResolution = voiceResolutionForLocale(synth, resolvedLocale);
  if (initialResolution.voice) return true;
  if (initialResolution.enumerated) return false;
  const waitMs = Math.max(0, Math.min(2_000, Number(timeoutMs) || 0));
  if (waitMs === 0 || signal?.aborted) return false;

  return new Promise((resolve) => {
    let settled = false;
    let timer = null;
    const priorHandler = typeof synth.onvoiceschanged === "function" ? synth.onvoiceschanged : null;
    const finish = (available) => {
      if (settled) return;
      settled = true;
      if (timer !== null) clearTimeout(timer);
      signal?.removeEventListener?.("abort", onAbort);
      synth.removeEventListener?.("voiceschanged", onVoicesChanged);
      if (!synth.removeEventListener && synth.onvoiceschanged === onVoicesChanged) {
        synth.onvoiceschanged = priorHandler;
      }
      resolve(available);
    };
    const onAbort = () => finish(false);
    const onVoicesChanged = (...args) => {
      priorHandler?.apply(synth, args);
      const resolution = voiceResolutionForLocale(synth, resolvedLocale);
      if (resolution.voice || resolution.enumerated) finish(Boolean(resolution.voice));
    };

    signal?.addEventListener?.("abort", onAbort, { once: true });
    if (typeof synth.addEventListener === "function") {
      synth.addEventListener("voiceschanged", onVoicesChanged);
    } else {
      synth.onvoiceschanged = onVoicesChanged;
    }
    timer = setTimeout(() => finish(hasCompatibleDeviceSpeechVoice(locale, overrides)), waitMs);
  });
}

export function createDeviceSpeechFallback(overrides = {}) {
  let generation = 0;
  let phase = "idle";
  let preparedPlan = null;
  let preparedCallbacks = {};
  let utterances = [];
  let completedIndexes = new Set();
  let lifecycleStartNotified = false;
  let attemptStarted = false;
  let pendingPausedRestart = false;
  let playbackRate = DEFAULT_SPEECH_RATE;
  let timelineDurationMs = 0;
  let timelineAnchorPositionMs = 0;
  let timelineAnchorNowMs = 0;
  let timelineTimer = null;
  let attemptStartTimer = null;
  let timelineAdvancePerMs = DEFAULT_SPEECH_RATE;
  let lastBoundaryPositionMs = null;
  let lastBoundaryNowMs = null;
  let timelineSegments = [];
  let totalTimelineTextLength = 0;

  const now =
    typeof overrides.now === "function"
      ? overrides.now
      : () =>
          typeof globalThis.performance?.now === "function"
            ? globalThis.performance.now()
            : Date.now();
  const scheduleInterval =
    typeof overrides.setInterval === "function" ? overrides.setInterval : globalThis.setInterval;
  const cancelInterval =
    typeof overrides.clearInterval === "function"
      ? overrides.clearInterval
      : globalThis.clearInterval;
  const scheduleTimeout =
    typeof overrides.setTimeout === "function" ? overrides.setTimeout : globalThis.setTimeout;
  const cancelTimeout =
    typeof overrides.clearTimeout === "function" ? overrides.clearTimeout : globalThis.clearTimeout;

  function supported() {
    return deviceSpeechRuntimeSupported(overrides);
  }

  function stopTimelineTicker() {
    if (timelineTimer !== null) cancelInterval(timelineTimer);
    timelineTimer = null;
  }

  function stopAttemptStartWatchdog() {
    if (attemptStartTimer !== null) cancelTimeout(attemptStartTimer);
    attemptStartTimer = null;
  }

  function resetTimelineCalibration() {
    timelineAdvancePerMs = playbackRate;
    lastBoundaryPositionMs = null;
    lastBoundaryNowMs = null;
  }

  function extrapolatedTimelinePosition(elapsedContentMs) {
    const anchor = Math.min(timelineDurationMs, Math.max(0, timelineAnchorPositionMs));
    const remainingMs = Math.max(0, timelineDurationMs - anchor);
    if (remainingMs === 0) return timelineDurationMs;
    if (remainingMs <= 1) return anchor;
    const reserveMs = Math.min(
      remainingMs,
      Math.min(
        MAX_TIMELINE_END_RESERVE_MS,
        Math.max(MIN_TIMELINE_END_RESERVE_MS, timelineDurationMs * TIMELINE_END_RESERVE_RATIO),
      ),
    );
    const linearWindowMs = remainingMs - reserveMs;
    if (elapsedContentMs <= linearWindowMs) return anchor + elapsedContentMs;
    const tailElapsedMs = elapsedContentMs - linearWindowMs;
    const tailProgressMs = reserveMs * (1 - Math.exp(-tailElapsedMs / reserveMs));
    return Math.min(timelineDurationMs - 1, anchor + linearWindowMs + tailProgressMs);
  }

  function timelinePosition() {
    if (timelineDurationMs <= 0) return 0;
    if (phase !== "active" || !attemptStarted) return timelineAnchorPositionMs;
    const elapsedMs = Math.max(0, now() - timelineAnchorNowMs);
    return extrapolatedTimelinePosition(elapsedMs * timelineAdvancePerMs);
  }

  function setTimelineAnchor(positionMs) {
    timelineAnchorPositionMs = Math.min(timelineDurationMs, Math.max(0, Number(positionMs) || 0));
    timelineAnchorNowMs = now();
  }

  function getTimeline() {
    if (!preparedPlan || timelineDurationMs <= 0) return null;
    return Object.freeze({
      positionMs: Math.round(timelinePosition()),
      durationMs: Math.round(timelineDurationMs),
      playbackRate,
      paused: phase === "paused",
    });
  }

  function emitTimeline() {
    const timeline = getTimeline();
    if (timeline) preparedCallbacks.onTimeline?.(timeline);
    return timeline;
  }

  function startTimelineTicker(requestGeneration) {
    stopTimelineTicker();
    if (typeof preparedCallbacks.onTimeline !== "function") return;
    timelineTimer = scheduleInterval(() => {
      if (generation !== requestGeneration || phase !== "active" || !attemptStarted) return;
      emitTimeline();
    }, DEVICE_SPEECH_TIMELINE_TICK_MS);
    timelineTimer?.unref?.();
  }

  function startAttemptStartWatchdog(requestGeneration) {
    stopAttemptStartWatchdog();
    attemptStartTimer = scheduleTimeout(() => {
      attemptStartTimer = null;
      if (generation !== requestGeneration || phase !== "active" || attemptStarted) return;
      failCurrent(new Error("device_speech_start_timeout"), requestGeneration);
    }, DEVICE_SPEECH_ATTEMPT_START_TIMEOUT_MS);
    attemptStartTimer?.unref?.();
  }

  function resetState({ clearPrepared, cancelSpeech }) {
    const { synth } = resolveRuntime(overrides);
    const retainedPosition = clearPrepared ? 0 : timelinePosition();
    generation += 1;
    stopTimelineTicker();
    stopAttemptStartWatchdog();
    phase = clearPrepared || !preparedPlan ? "idle" : "prepared";
    utterances = [];
    completedIndexes = new Set();
    lifecycleStartNotified = false;
    attemptStarted = false;
    pendingPausedRestart = false;
    resetTimelineCalibration();
    if (clearPrepared) {
      preparedPlan = null;
      preparedCallbacks = {};
      timelineDurationMs = 0;
      timelineSegments = [];
      totalTimelineTextLength = 0;
    }
    setTimelineAnchor(retainedPosition);
    if (cancelSpeech) {
      try {
        synth?.cancel?.();
      } catch {
        /* cancellation is best-effort */
      }
      try {
        if (synth?.paused) synth.resume();
      } catch {
        /* restoring the shared synthesis pause state is best-effort */
      }
    }
  }

  function cancelCurrentAttempt() {
    const { synth } = resolveRuntime(overrides);
    generation += 1;
    stopTimelineTicker();
    stopAttemptStartWatchdog();
    utterances = [];
    completedIndexes = new Set();
    attemptStarted = false;
    resetTimelineCalibration();
    try {
      synth?.cancel?.();
    } catch {
      /* cancellation is best-effort */
    }
    try {
      if (synth?.paused) synth.resume();
    } catch {
      /* restoring the shared synthesis pause state is best-effort */
    }
  }

  function failCurrent(error, requestGeneration) {
    if (generation !== requestGeneration) return;
    const callbacks = preparedCallbacks;
    resetState({ clearPrepared: false, cancelSpeech: true });
    callbacks.onError?.(error);
  }

  function prepare(plan, callbacks = {}) {
    const normalized = normalizedPlan(plan);
    if (!normalized || !supported()) return false;
    if (phase === "active" || phase === "paused" || utterances.length > 0) return false;
    const { synth } = resolveRuntime(overrides);
    const voiceResolution = voiceResolutionForLocale(synth, normalized.lang);
    if (!voiceResolution.voice) return false;
    generation += 1;
    phase = "prepared";
    utterances = [];
    completedIndexes = new Set();
    lifecycleStartNotified = false;
    attemptStarted = false;
    pendingPausedRestart = false;
    playbackRate = normalizeRate(callbacks.rate ?? playbackRate);
    resetTimelineCalibration();
    preparedPlan = normalized;
    timelineDurationMs = estimateDeviceSpeechDurationMs(normalized);
    const timeline = timelineSegmentsForPlan(normalized);
    timelineSegments = timeline.segments;
    totalTimelineTextLength = timeline.totalTextLength;
    setTimelineAnchor(0);
    preparedCallbacks = {
      onStart: typeof callbacks.onStart === "function" ? callbacks.onStart : undefined,
      onEnd: typeof callbacks.onEnd === "function" ? callbacks.onEnd : undefined,
      onError: typeof callbacks.onError === "function" ? callbacks.onError : undefined,
      onTimeline: typeof callbacks.onTimeline === "function" ? callbacks.onTimeline : undefined,
    };
    return true;
  }

  function queueSpeechFromPosition(positionMs, { clearIfVoiceMissing = false } = {}) {
    if (!preparedPlan || !supported()) return false;
    const { synth, Utterance } = resolveRuntime(overrides);
    generation += 1;
    const requestGeneration = generation;
    completedIndexes = new Set();
    attemptStarted = false;
    pendingPausedRestart = false;
    resetTimelineCalibration();
    setTimelineAnchor(positionMs);
    try {
      const voiceResolution = voiceResolutionForLocale(synth, preparedPlan.lang);
      if (!voiceResolution.voice) {
        const callbacks = preparedCallbacks;
        resetState({ clearPrepared: clearIfVoiceMissing, cancelSpeech: false });
        if (!clearIfVoiceMissing) {
          callbacks.onError?.(new Error("device_speech_voice_unavailable"));
        }
        return false;
      }
      const selectedVoice = voiceResolution.voice;
      const speechEntries = speechEntriesFromPosition(
        timelineSegments,
        totalTimelineTextLength,
        timelineDurationMs,
        positionMs,
      );
      if (speechEntries.length === 0) return false;
      utterances = speechEntries.map((entry, index) => {
        const utterance = new Utterance(entry.text);
        utterance.lang = preparedPlan.lang;
        utterance.rate = playbackRate;
        if (selectedVoice) utterance.voice = selectedVoice;
        utterance.onstart = () => {
          if (generation !== requestGeneration) return;
          if (!attemptStarted) {
            attemptStarted = true;
            stopAttemptStartWatchdog();
            setTimelineAnchor(timelineAnchorPositionMs);
            startTimelineTicker(requestGeneration);
          }
          const entryStartPosition =
            totalTimelineTextLength > 0
              ? (entry.absoluteStart / totalTimelineTextLength) * timelineDurationMs
              : timelineAnchorPositionMs;
          lastBoundaryPositionMs = entryStartPosition;
          lastBoundaryNowMs = now();
          if (!lifecycleStartNotified) {
            lifecycleStartNotified = true;
            preparedCallbacks.onStart?.();
          }
          emitTimeline();
        };
        utterance.onboundary = (event) => {
          if (generation !== requestGeneration || !Number.isFinite(Number(event?.charIndex)))
            return;
          const absoluteTextIndex = Math.min(
            entry.absoluteEnd,
            entry.absoluteStart + Math.max(0, Number(event.charIndex)),
          );
          const mappedPosition =
            totalTimelineTextLength > 0
              ? (absoluteTextIndex / totalTimelineTextLength) * timelineDurationMs
              : 0;
          const boundaryNowMs = now();
          const renderedPosition = timelinePosition();
          if (
            lastBoundaryPositionMs !== null &&
            lastBoundaryNowMs !== null &&
            mappedPosition > lastBoundaryPositionMs &&
            boundaryNowMs - lastBoundaryNowMs >= MIN_BOUNDARY_CALIBRATION_INTERVAL_MS
          ) {
            const observedAdvancePerMs =
              (mappedPosition - lastBoundaryPositionMs) / (boundaryNowMs - lastBoundaryNowMs);
            const minimumAdvancePerMs = Math.max(0.05, playbackRate * 0.1);
            const maximumAdvancePerMs = Math.max(minimumAdvancePerMs, playbackRate * 4);
            const boundedAdvancePerMs = Math.min(
              maximumAdvancePerMs,
              Math.max(minimumAdvancePerMs, observedAdvancePerMs),
            );
            timelineAdvancePerMs = timelineAdvancePerMs * 0.65 + boundedAdvancePerMs * 0.35;
          }
          lastBoundaryPositionMs = mappedPosition;
          lastBoundaryNowMs = boundaryNowMs;
          setTimelineAnchor(Math.max(renderedPosition, mappedPosition));
          emitTimeline();
        };
        utterance.onend = () => {
          if (generation !== requestGeneration) return;
          const mappedPosition =
            totalTimelineTextLength > 0
              ? (entry.absoluteEnd / totalTimelineTextLength) * timelineDurationMs
              : timelineDurationMs;
          setTimelineAnchor(Math.max(timelinePosition(), mappedPosition));
          emitTimeline();
          completedIndexes.add(index);
          if (completedIndexes.size !== utterances.length) return;
          const onEnd = preparedCallbacks.onEnd;
          setTimelineAnchor(timelineDurationMs);
          emitTimeline();
          resetState({ clearPrepared: true, cancelSpeech: false });
          onEnd?.();
        };
        utterance.onerror = (event) => failCurrent(event, requestGeneration);
        return utterance;
      });
      phase = "active";
      startAttemptStartWatchdog(requestGeneration);
      utterances.forEach((utterance) => synth.speak(utterance));
      emitTimeline();
      return true;
    } catch (error) {
      failCurrent(error, requestGeneration);
      return false;
    }
  }

  function playPrepared() {
    if (phase !== "prepared" || !preparedPlan || !supported()) return false;
    // WebKit authorizes speech at speak() time. Construct and queue the real
    // text synchronously in the follow-up user gesture; an idle pause is not
    // sticky on iOS and cannot safely reserve that authorization earlier.
    return queueSpeechFromPosition(timelineAnchorPositionMs, { clearIfVoiceMissing: true });
  }

  function pause() {
    if (phase !== "active") return false;
    const { synth } = resolveRuntime(overrides);
    const positionMs = timelinePosition();
    try {
      synth.pause();
      phase = "paused";
      stopTimelineTicker();
      stopAttemptStartWatchdog();
      setTimelineAnchor(positionMs);
      emitTimeline();
      return true;
    } catch {
      return false;
    }
  }

  function resume() {
    if (phase !== "paused") return false;
    const { synth } = resolveRuntime(overrides);
    if (pendingPausedRestart) {
      phase = "prepared";
      return queueSpeechFromPosition(timelineAnchorPositionMs);
    }
    try {
      synth.resume();
      phase = "active";
      setTimelineAnchor(timelineAnchorPositionMs);
      if (!attemptStarted) startAttemptStartWatchdog(generation);
      startTimelineTicker(generation);
      emitTimeline();
      return true;
    } catch (error) {
      failCurrent(error, generation);
      return false;
    }
  }

  function resetToPrepared() {
    if (!preparedPlan) return false;
    const cancelSpeech = utterances.length > 0 || phase === "active" || phase === "paused";
    resetState({ clearPrepared: false, cancelSpeech });
    return true;
  }

  function stop() {
    const hadSpeech = Boolean(preparedPlan || utterances.length || phase !== "idle");
    const cancelSpeech = utterances.length > 0 || phase === "active" || phase === "paused";
    resetState({ clearPrepared: true, cancelSpeech });
    return hadSpeech;
  }

  function discard() {
    return stop();
  }

  function setRate(value) {
    const nextRate = normalizeRate(value);
    if (nextRate === playbackRate) return playbackRate;
    const positionMs = timelinePosition();
    playbackRate = nextRate;
    if (phase === "active") {
      cancelCurrentAttempt();
      phase = "prepared";
      queueSpeechFromPosition(positionMs);
    } else if (phase === "paused") {
      cancelCurrentAttempt();
      phase = "paused";
      pendingPausedRestart = true;
      setTimelineAnchor(positionMs);
      emitTimeline();
    }
    return playbackRate;
  }

  function seekToPositionMs(value) {
    if (!preparedPlan || (phase !== "active" && phase !== "paused")) return false;
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || timelineDurationMs <= 0) return false;
    const targetPositionMs = Math.min(timelineDurationMs, Math.max(0, numeric));
    if (targetPositionMs >= timelineDurationMs) {
      const onEnd = preparedCallbacks.onEnd;
      cancelCurrentAttempt();
      setTimelineAnchor(timelineDurationMs);
      emitTimeline();
      resetState({ clearPrepared: true, cancelSpeech: false });
      onEnd?.();
      return true;
    }
    if (phase === "paused") {
      cancelCurrentAttempt();
      phase = "paused";
      pendingPausedRestart = true;
      setTimelineAnchor(targetPositionMs);
      emitTimeline();
      return true;
    }
    cancelCurrentAttempt();
    phase = "prepared";
    if (queueSpeechFromPosition(targetPositionMs)) return true;
    return false;
  }

  function hasPrepared(key) {
    if (!preparedPlan) return false;
    return key === undefined || preparedPlan.key === String(key);
  }

  function active() {
    return phase === "active" || phase === "paused";
  }

  return Object.freeze({
    supported,
    prepare,
    playPrepared,
    resetToPrepared,
    pause,
    resume,
    stop,
    discard,
    setRate,
    seekToPositionMs,
    getTimeline,
    hasPrepared,
    active,
  });
}
